import { useRef, useState, useCallback, ReactNode, ReactElement, useEffect } from 'react';
import { READY_STATES, useLazyLoadMedia } from 'dibs-elements/exports/useLazyLoadMedia';
import { MediaControls, CommonControlsProps } from 'dibs-elements/exports/MediaControls';

const SPACE_KEY = ' ' as const;

/** Do not allow autoPlay = true, muted = false */
type AutoPlayArgs =
    | {
          /** If initial `muted` prop is unknown or `true`, `autoPlay` cannot be `true`. Will set an undefined initial `muted` prop to false. */
          autoPlay: false;
          /** Initial `muted` prop. Non-autoPlay videos may be muted or unmuted initially. */
          muted?: boolean;
      }
    | {
          /** Defaults to true. Will set an `undefined` `muted` prop to `true`. */
          autoPlay?: true;
          /** Initial `muted` prop. Defaults to `true`. AutoPlay videos must be initially muted. */
          muted?: true;
      };

/** CSS `<length>` as string (e.g. `100px`, `30vw, etc.). */

export type ControlHandlers = {
    onFullScreenOpen?: () => void;
    onFullScreenClose?: () => void;
    onPause?: () => void;
    onPlay?: () => void;
    onReplay?: () => void;
    onMute?: () => void;
    onUnmute?: () => void;
};

type VideoProps = {
    children?: ReactNode;
    src?: string | null;
    /** will default to controls on hover */
    controls?: 'always' | 'never' | boolean;
    keyboardShortcuts?: boolean;
    controlHandlers?: ControlHandlers;
    /** unit-less CSS `<integer>` pixel value as string */
    width?: string;
    /** unit-less CSS `<integer>` pixel value as string */
    height?: string;
    /** CSS `<length>` as string (e.g. `100px`, `30vw, etc.). */
    maxWidth?: string;
    /** CSS `<length>` as string (e.g. `100px`, `30vw, etc.). */
    maxHeight?: string;
    preload?: 'none' | 'auto' | 'metadata';
} & AutoPlayArgs;

export const Video = <T extends string = '', U extends string = ''>({
    src,
    controls = true,
    height,
    width,
    maxWidth,
    maxHeight,
    autoPlay = true,
    muted: mutedProp = autoPlay || true,
    children,
    placeholder,
    controlHandlers,
    keyboardShortcuts,
    preload,
    ...controlsProps
}: VideoProps & CommonControlsProps<T, U>): ReactElement => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const videoContainerRef = useRef<HTMLElement>(null);
    const [muted, setMuted] = useState(mutedProp);
    const [paused, setPaused] = useState(!autoPlay);
    const lazyLoad = useLazyLoadMedia('video', {
        ref: videoRef,
        width,
        height,
        maxWidth,
        maxHeight,
        shimmer: placeholder === 'shimmer',
        excludedHandlers: 'onSuspend onWaiting',
    });
    const { onFullScreenOpen, onFullScreenClose, onPlay, onPause, onReplay, onMute, onUnmute } =
        controlHandlers || {};

    const handlePlay = useCallback(() => {
        setPaused(false);
    }, []);
    const handlePause = useCallback(() => {
        setPaused(true);
    }, []);
    const handleVolumeChange = useCallback(() => {
        if (videoRef.current) {
            setMuted(videoRef.current.muted);
        }
    }, []);

    const handlePlayPause = useCallback(() => {
        if (videoRef.current?.paused) {
            onPlay?.();
            videoRef.current.play();
            setPaused(false);
        } else {
            videoRef.current?.pause();
            onPause?.();
            setPaused(true);
        }
    }, [onPause, onPlay]);

    const handleReplay = useCallback(() => {
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            onReplay?.();
        }
    }, [onReplay]);

    const handleMute = useCallback(() => {
        if (videoRef.current) {
            if (muted && onUnmute) {
                onUnmute();
            } else if (!muted && onMute) {
                onMute();
            }
            videoRef.current.muted = !muted;
            setMuted(!muted);
        }
    }, [muted, onMute, onUnmute]);

    useEffect(() => {
        function handleSpace(e: KeyboardEvent): void {
            const isVideoActiveElement =
                document.activeElement === document.body ||
                document.activeElement === videoRef.current ||
                document.activeElement === videoContainerRef.current;

            if (e.key === SPACE_KEY && videoRef.current && isVideoActiveElement) {
                handlePlayPause();
                // prevent from bubbling up to other handlers
                e.preventDefault();
                e.stopImmediatePropagation();
                e.stopPropagation();
            }
        }
        if (keyboardShortcuts) {
            window.addEventListener('keydown', handleSpace);
        }
        return () => window.removeEventListener('keydown', handleSpace);
    }, [handlePlayPause, keyboardShortcuts]);

    const isLoading = lazyLoad.readyState < READY_STATES.HAVE_FUTURE_DATA;
    const isPlayable = !isLoading || preload === 'none';
    const showControls = isPlayable && controls;

    return (
        <MediaControls
            mediaRef={videoRef}
            mediaContainerRef={videoContainerRef}
            onFullScreenOpen={onFullScreenOpen}
            onFullScreenClose={onFullScreenClose}
            onPlay={handlePlayPause}
            onReplay={handleReplay}
            onMute={handleMute}
            controls={controls === 'never' ? 'never' : showControls}
            isMuted={muted}
            isPaused={paused}
            isLoading={isLoading}
            placeholder={placeholder}
            {...controlsProps}
        >
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
                {...lazyLoad.styleProps}
                {...lazyLoad.handlerProps}
                preload={preload}
                height={height}
                width={width}
                autoPlay={autoPlay}
                muted={mutedProp}
                playsInline
                loop
                controls={false}
                onPlay={handlePlay}
                onPause={handlePause}
                onVolumeChange={handleVolumeChange}
                ref={videoRef}
            >
                {src && <source src={src} />}
                {children}
            </video>
        </MediaControls>
    );
};
